import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { companyInitials } from './company-identity.util';

/**
 * The company identity block in the app shell (Sentry DISTRODOT-PORTAL-MR): "you are here" for a
 * carrier — the company's logo (or two initials on a brand-coloured tile), its name, and a second
 * line (the signed-in user, or "Bekeken als <naam>"). It is the CONTENT of the user-menu trigger
 * (`UserComponent`): not interactive itself, no tabstop, no accessible name of its own — the button
 * around it carries aria-label and tooltip. `chevron` draws the menu affordance on the right;
 * `impersonating` adds a back badge on the avatar and colours the second line with the accent;
 * `compact` keeps only the avatar (small-screen bar).
 *
 * Presentational: inputs only. The logo is decorative (`alt=""`). Light/dark: the logo follows
 * the `only-light-theme` / `only-dark-theme` idiom of `app-logo` (switched by the `.dark` class
 * LayoutComponent stamps on `<html>`/`<body>`); one configured logo serves both schemes rather
 * than falling back to initials in the other one. Colours are tokens only — the tile is the brand
 * colour with its computed on-colour (`--fuse-primary` / `--fuse-on-primary`), the muted line
 * `--dd-card-muted-fg`.
 */
@Component({
  selector: 'dd-company-identity',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'dd-company-identity',
    '[class.dd-company-identity--compact]': 'compact()',
    '[class.dd-company-identity--impersonating]': 'impersonating()',
    '[style.--dd-company-initials-fg-light]': 'initialsColorLight()',
    '[style.--dd-company-initials-fg-dark]': 'initialsColorDark()',
  },
  styles: `
    :host {
      /* Brand colour behind the INITIALS only. The text colour is black or white by WCAG luminance
         (initialsTextColorFor, passed in per scheme); without it the app-wide YIQ pick
         --fuse-on-primary. The numbered --fuse-primary-N shades are generated from the brand colour
         and are not lightness-ordered (measured 2026-10-21: -100 = #720092, -800 = #b457c5), so a
         "tint + dark text" pair built from them cannot promise 4.5:1. */
      --dd-company-avatar-bg: var(--fuse-primary);
      --dd-company-avatar-fg: var(--dd-company-initials-fg-light, var(--fuse-on-primary, #ffffff));
      display: flex;
      min-width: 0;
    }
    :host-context(.dark) {
      --dd-company-avatar-fg: var(--dd-company-initials-fg-dark, var(--fuse-on-primary, #ffffff));
    }

    /* Not named "block": Tailwind runs with important: true, and its .block utility would turn
       this flex row into a stacked block (measured 2026-10-21: avatar above the text, outside the bar). */
    .identity {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
      color: inherit;
      text-align: left;
    }

    .avatar {
      flex: none;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 6px;
      overflow: hidden;
      background: var(--dd-company-avatar-bg);
      color: var(--dd-company-avatar-fg);
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
    }
    /* A logo sits on the card colour with a little air: on the brand colour a transparent or
       brand-coloured mark would drown. */
    .avatar--logo {
      background: var(--fuse-bg-card);
      padding: 2px;
    }
    .avatar img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    .person {
      width: 16px;
      height: 16px;
    }

    .text {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    .name {
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
      max-width: 160px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .subline {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      color: var(--dd-card-muted-fg);
      max-width: 160px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .chevron {
      flex: none;
      width: 16px;
      height: 16px;
      color: var(--dd-card-muted-fg);
    }

    /* Impersonation: a small round back badge bottom-right ON the avatar (accent glyph on the
       accent surface, --dd-accent-* tokens with their dark variants in _widgets.scss) and the
       second line in the accent foreground. The trigger adds its accent border; no warning tokens
       here, the full-width banner carries that signal. */
    .avatar-wrap {
      position: relative;
      flex: none;
      display: flex;
    }
    .back {
      position: absolute;
      right: -3px;
      bottom: -3px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--dd-accent-surface);
      color: var(--dd-accent-glyph);
    }
    .back svg {
      width: 9px;
      height: 9px;
    }
    :host(.dd-company-identity--impersonating) .subline {
      color: var(--dd-accent-fg);
    }

    /* 160px keeps the trigger at ~238px (product ruling 2026-10-22: 220px read as too wide with
       long names; the full text lives in tooltip and aria-label). Below the xl breakpoint (1440px,
       tailwind.config.js) the modern bar also has to fit the main navigation beside this trigger
       (measured 2026-10-21 at 1280px on the carrier tenant, 15 items: 4.5px menu text with a 36px
       trigger, 2px with a 298px one), so the name shortens further; below 960px the bar shows the
       avatar alone anyway. */
    @media (max-width: 1439px) {
      .name,
      .subline {
        max-width: 120px;
      }
    }
  `,
  template: `
    <span class="identity" data-cy="company-identity">
      <span class="avatar-wrap">
        <span class="avatar" [class.avatar--logo]="hasLogo()" data-cy="company-identity-avatar" aria-hidden="true">
          @if (hasLogo()) {
            <img class="only-light-theme" data-cy="company-identity-logo" alt="" [src]="logoLightSrc() ?? logoDarkSrc()" />
            <img class="only-dark-theme" data-cy="company-identity-logo" alt="" [src]="logoDarkSrc() ?? logoLightSrc()" />
          } @else if (initials()) {
            <span data-cy="company-identity-initials">{{ initials() }}</span>
          } @else {
            <!-- heroicons outline user: nothing to take initials from -->
            <svg class="person" data-cy="company-identity-person" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.118a7.5 7.5 0 0 1 15 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.5-1.632Z" />
            </svg>
          }
        </span>
        @if (impersonating()) {
          <!-- heroicons outline arrow-uturn-left: the way back to your own account -->
          <span class="back" data-cy="company-identity-back" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
            </svg>
          </span>
        }
      </span>
      @if (!compact()) {
        <span class="text">
          <span class="name" data-cy="company-identity-name">{{ name() }}</span>
          @if (subline()) {
            <span class="subline" data-cy="company-identity-subline">{{ subline() }}</span>
          }
        </span>
        @if (chevron()) {
          <svg
            class="chevron"
            data-cy="company-identity-chevron"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        }
      }
    </span>
  `,
})
export class CompanyIdentityComponent {
  /** Top line: the company's display name (or the user's, when there is no company). */
  readonly name = input.required<string>();
  /** Light-scheme company logo (`entry.branding.light.logo.blobUrl`). Null = initials, or the dark logo. */
  readonly logoLightSrc = input<string | null>(null);
  /** Dark-scheme company logo (`entry.branding.dark.logo.blobUrl`). */
  readonly logoDarkSrc = input<string | null>(null);
  /** Muted second line: the signed-in user, or "Bekeken als <naam>". Null = one line. */
  readonly subline = input<string | null>(null);
  /** Draw the menu chevron on the right (the trigger opens a menu). Ignored in compact mode. */
  readonly chevron = input(false);
  /**
   * An admin is viewing as someone else: a small back badge sits on the avatar and the second line
   * ("Bekeken als <naam>") takes the accent colour. The badge stays in compact mode.
   */
  readonly impersonating = input(false);
  /** Avatar only (small-screen bar). */
  readonly compact = input(false);
  /** Initials colour per scheme (`initialsTextColorFor`); null = the CSS fallback --fuse-on-primary. */
  readonly initialsColorLight = input<string | null>(null);
  readonly initialsColorDark = input<string | null>(null);

  readonly hasLogo = computed(() => !!(this.logoLightSrc() || this.logoDarkSrc()));
  /** Two letters, or empty when the name has none: the tile then shows a person glyph. */
  readonly initials = computed(() => {
    const initials = companyInitials(this.name());
    return initials === '?' ? '' : initials;
  });
}
